<template>
  <div
    class="image-viewer"
    :class="ns.b()"
    :style="{ width: props.width + 'px', height: props.height + 'px' }"
  >
    <div @click.stop @wheel.prevent="handleWheel" @mousedown="handleMouseDown">
      <div :style="imageContainerStyle">
        <div :style="imageStyle" :class="ns.e('img')">
          <slot />
        </div>
      </div>
      <div :class="[ns.e(`controls-${controlsPosition}`), ns.e('controls')]">
        <lt-icon class="icon" title="缩小" @click="zoomOut">
          <ZoomOut />
        </lt-icon>
        <lt-icon title="放大" @click="zoomIn">
          <ZoomIn />
        </lt-icon>
        <lt-icon title="全屏" @click="toggleFullScreen">
          <FullScreen />
        </lt-icon>
        <lt-icon title="向左旋转" @click="rotateLeft">
          <RefreshLeft />
        </lt-icon>
        <lt-icon title="向右旋转" @click="rotateRight">
          <RefreshRight />
        </lt-icon>
        <lt-icon title="还原" @click="reset"><ScaleToOriginal /></lt-icon>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import {
  FullScreen,
  RefreshLeft,
  RefreshRight,
  ScaleToOriginal,
  ZoomIn,
  ZoomOut,
} from '@inte-cloud/icons-vue'
import { viewerProps } from './viewer'

defineOptions({
  name: 'LtViewer',
})

const ns = useNamespace('viewer')
const props = defineProps(viewerProps)

const scale = ref<number>(1)
const rotation = ref<number>(0)
const flip = ref<boolean>(false)
const isDragging = ref<boolean>(false)
const dragStartX = ref<number>(0)
const dragStartY = ref<number>(0)
const translateX = ref<number>(0)
const translateY = ref<number>(0)

const zoomIn = () => {
  if (scale.value < props.maxScale) {
    scale.value += 0.1
  }
}

const zoomOut = () => {
  if (scale.value > props.minScale) {
    scale.value -= 0.1
  }
}

const rotateLeft = () => {
  rotation.value -= 90
}

const rotateRight = () => {
  rotation.value += 90
}

const toggleFullScreen = () => {
  const imgElement = document.querySelector('.image-viewer img')
  if (imgElement) {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      imgElement.requestFullscreen()
    }
  }
}

const reset = () => {
  scale.value = 1
  rotation.value = 0
  flip.value = false
  translateX.value = 0
  translateY.value = 0
}

const imageStyle = computed(() => ({
  transform: `scale(${scale.value}) rotate(${rotation.value}deg) ${
    flip.value ? 'scaleX(-1)' : ''
  }`,
}))

const imageContainerStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px)`,
}))

const handleWheel = (event: any) => {
  if (event.deltaY > 0) {
    zoomOut()
  } else {
    zoomIn()
  }
}

const handleMouseDown = (event: any) => {
  isDragging.value = true
  dragStartX.value = event.clientX - translateX.value
  dragStartY.value = event.clientY - translateY.value
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

const handleMouseMove = (event: any) => {
  if (isDragging.value) {
    translateX.value = event.clientX - dragStartX.value
    translateY.value = event.clientY - dragStartY.value
  }
}

const handleMouseUp = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
}

onMounted(() => {
  // 添加被动事件监听器
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  // 移除事件监听器
  window.removeEventListener('scroll', handleScroll)
})

const handleScroll = () => {
  // 处理滚动事件
  console.log('Scrolling...')
}
defineExpose({
  reset,
})
</script>
